import { MouseEvent, useEffect, useState } from 'react';

/**
 * The V2 seam onto CookieYes, which arrives through GTM and is the only thing that knows
 * which privacy law applies to a visitor. The legacy side has the same seam in
 * `public/resources/main/js/modules/cookieYes.js`, which the privacy policies use.
 */
declare global {
  interface Window {
    /**
     * CookieYes' documented consent API, defined by the `banner.js` GTM loads. Typed down
     * to the one field read here; the full shape is in CookieYes' docs.
     *
     * `activeLaw` is the privacy law CookieYes resolved for this visitor's location —
     * `'ccpa'` for the US states with opt-out laws, `'gdpr'` in Europe, and the empty
     * string where CookieYes decided none applies.
     */
    getCkyConsent?: () => { activeLaw?: string } | undefined;

    /** Opens CookieYes' preference centre. The legacy `cookieYes.js` module calls this too. */
    revisitCkyConsent?: () => void;
  }
}

/**
 * How often to ask CookieYes whether it has resolved a law yet. See below for why this
 * is a poll and not a subscription.
 */
const POLL_INTERVAL_MS = 100;

/**
 * When to stop polling and leave it to `cookieyes_banner_load`. Not a deadline for the
 * answer — the listener outlives this and has no deadline of its own.
 */
const POLL_TIMEOUT_MS = 10000;

function activeLawIsKnown(): boolean {
  return Boolean(window.getCkyConsent?.()?.activeLaw);
}

/**
 * Whether CookieYes reports a privacy law in force for this visitor's location.
 *
 * This is the geo gate for anything that may only be shown where it is legally required —
 * today, the "Do Not Sell or Share My Personal Information" footer link. CookieYes
 * resolves the visitor's region on its own CDN and tells us which law applies, so the list
 * of states is theirs to keep current rather than ours: nothing here needs changing when a
 * new state law lands.
 *
 * Any `activeLaw` counts, not just `ccpa`. A visitor under GDPR has consent choices to
 * make too, and the link opens the same preference centre for them. Narrowing this to the
 * US laws would mean holding a list of CookieYes' own law codes, which is the maintenance
 * this hook exists to avoid.
 *
 * ## Why this polls
 *
 * CookieYes publishes `cookieyes_banner_load`, and that would be the obvious thing to
 * subscribe to. Measured against the live banner on a DiscountMags page, it is the *last*
 * of CookieYes' signals rather than the first — the answer is readable 69–199ms before the
 * event announces it:
 *
 * | signal                                 | run 1  | run 2  | run 3  |
 * | -------------------------------------- | ------ | ------ | ------ |
 * | V2 footer mounted                      |  438ms |  257ms |  208ms |
 * | `getCkyConsent` defined (returns `''`) | 1078ms |  978ms | 1329ms |
 * | `getCkyConsent().activeLaw` answers    | 1379ms | 1240ms | 1623ms |
 * | `cookieyes_banner_load` fires          | 1555ms | 1439ms | 1692ms |
 *
 * So the poll is what makes the link appear as early as it can, and the event is the
 * safety net: it is the only signal with no deadline, so a CookieYes that arrives after
 * the poll has given up still reveals the link. That matters more than the milliseconds —
 * the version this replaces polled for ten seconds and then gave up for good, which on a
 * slow load meant no link at all on a page that legally required one.
 *
 * Note the third row. `getCkyConsent` exists for ~300ms before it knows anything, and
 * returns `activeLaw: ''` in the meantime — so "the function is defined" is not the
 * signal, and reading it once on arrival would quietly conclude that no law applies.
 */
export function useConsentLawActive(): boolean {
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (active) {
      return;
    }

    const check = () => {
      if (activeLawIsKnown()) {
        setActive(true);
      }
    };

    // CookieYes may already have answered — on a second mount, or a page that took long
    // enough to render. The interval's first tick is a whole period away.
    check();

    const interval = window.setInterval(check, POLL_INTERVAL_MS);
    const timeout = window.setTimeout(() => window.clearInterval(interval), POLL_TIMEOUT_MS);

    document.addEventListener('cookieyes_banner_load', check);

    // Runs on unmount, and again the moment `active` flips — which is what stops the
    // timers, so `check` does not need to tear itself down.
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
      document.removeEventListener('cookieyes_banner_load', check);
    };
  }, [active]);

  return active;
}

/**
 * Opens CookieYes' preference centre instead of following the link.
 *
 * Deliberately not CookieYes' own `cky-banner-element` class, which is the integration
 * their docs hand you. That class works — CookieYes binds it by delegation, so it catches
 * a link that appears late, as a gated one always does — but it does **not** stop the
 * browser following the href, so the preference centre opens and is destroyed by the
 * navigation a moment later. Measured on the live banner: with the class alone the click
 * lands on `/privacy#your-choices` and no modal survives it.
 *
 * Carrying the class *as well* would be worse than not carrying it. The one case the href
 * exists for is CookieYes being present without `revisitCkyConsent` — and in exactly that
 * case the class would open the preference centre and then navigate away from it again.
 *
 * So: open it ourselves and stop the navigation, or do neither and let the href take the
 * visitor to the section of the policy describing the choice. `revisitCkyConsent` is the
 * same global the legacy `cookieYes.js` module calls from the privacy policies.
 */
export function openConsentPreferences(event: MouseEvent<HTMLAnchorElement>): void {
  if (!window.revisitCkyConsent) {
    return;
  }

  event.preventDefault();
  window.revisitCkyConsent();
}
